import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/customer',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: '登录',
      noTagsView: true
    }
  },

  {
    path: '/403',
    component: () => import('@/views/Error/403.vue'),
    name: 'NoFind403',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind404',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  },
  {
    path: '/500',
    component: () => import('@/views/Error/500.vue'),
    name: 'NoFind500',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

// 前端控制的动态路由: 若后端没有返回路由，则使用此静态路由
export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/customer',
    name: 'CustomerCenter',
    meta: {
      title: '消息中心',
      icon: 'ant-design:customer-service-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'customer',
        component: () => import('@/views/Customer/Reply/Reply.vue'),
        name: 'Customer',
        meta: {
          title: '客服管理',
          hidden: false
        }
      }
    ]
  },
  {
    path: '/chat',
    component: Layout,
    redirect: '/chat/group-list',
    name: 'Chat',
    meta: {
      title: '聊天管理',
      icon: 'material-symbols:chat-bubble-sharp',
      alwaysShow: true
    },
    children: [
      {
        path: 'group-list',
        component: () => import('@/views/Chat/GroupList/GroupList.vue'),
        name: 'GroupList',
        meta: {
          title: '成员列表'
        }
      },
      {
        path: 'group-records',
        component: () => import('@/views/Chat/GroupChatRecords/GroupChatRecords.vue'),
        name: 'GroupChatRecords',
        meta: {
          title: '聊天大厅'
        }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    redirect: '/system/system-config',
    name: 'System',
    meta: {
      title: '系统管理',
      icon: 'eos-icons:cluster-management-outlined',
      alwaysShow: true
    },
    children: [
      {
        path: 'config',
        component: () => import('@/views/System/Config/Config.vue'),
        name: 'SystemConfig',
        meta: {
          title: '常规配置'
        }
      }
    ]
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: '个人',
      hidden: false,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: '个人中心',
          hidden: false,
          canTo: true
        }
      }
    ]
  },

  {
    path: '/error',
    component: Layout,
    redirect: '/error/404',
    name: 'Error',
    meta: {
      title: t('router.errorPage'),
      hidden: true
    },
    children: [
      {
        path: '404-demo',
        component: () => import('@/views/Error/404.vue'),
        name: '404Demo',
        meta: {
          title: '404'
        }
      },
      {
        path: '403-demo',
        component: () => import('@/views/Error/403.vue'),
        name: '403Demo',
        meta: {
          title: '403'
        }
      },
      {
        path: '500-demo',
        component: () => import('@/views/Error/500.vue'),
        name: '500Demo',
        meta: {
          title: '500'
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
